import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HardHat, Shield, Loader2, Mic, ChevronRight, Share2, ArrowLeft, Mail, Lock, User, Phone, Building2, MessageSquare, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import ShareModal from '../components/ui/share-modal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Get Started form state (matching the modal)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    message: ''
  });

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      navigate(user.role === 'ADMIN' ? '/admin' : '/tradie');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/setup-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      // Fallback to mailto if API fails
      const mailtoLink = `mailto:tradeofficeai@gmail.com?subject=2Tap Setup Request from ${formData.name}&body=Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone || 'Not provided'}%0D%0ABusiness: ${formData.businessName || 'Not provided'}%0D%0A%0D%0AMessage:%0D%0A${formData.message || 'I would like to request a 2Tap setup.'}`;
      window.location.href = mailtoLink;
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (type) => {
    if (type === 'tradie') {
      setLoginEmail('tradie@demo.com');
      setLoginPassword('demo123');
    } else {
      setLoginEmail('admin@demo.com');
      setLoginPassword('admin123');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header - Matching Landing Page */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            data-testid="back-to-home-btn"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <button
            onClick={handleShare}
            data-testid="share-btn"
            className="flex items-center gap-1.5 text-slate-600 hover:text-slate-900 font-medium text-sm"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Visual Element - Subtle background */}
        <div className="bg-[#1F2A37] py-8 sm:py-12 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-md mx-auto px-6 text-center">
            {/* Animated Mic */}
            <div className="inline-flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <Mic className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={1.5} />
                </div>
                {/* Pulse effect */}
                <div className="absolute inset-0 bg-orange-500/30 rounded-2xl animate-ping"></div>
              </div>
            </div>
            
            <h1 
              className="text-2xl sm:text-3xl font-black text-white mb-2 tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WELCOME BACK
            </h1>
            <p className="text-slate-300 text-sm sm:text-base">
              Sign in to your 2Tap account
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="flex-1 px-4 py-6 sm:py-8 -mt-4">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 p-1.5 rounded-xl h-14">
                <TabsTrigger 
                  value="login" 
                  data-testid="login-tab"
                  className="rounded-lg font-bold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  SIGN IN
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  data-testid="register-tab"
                  className="rounded-lg font-bold text-base data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  GET STARTED
                </TabsTrigger>
              </TabsList>

              {/* Sign In Form */}
              <TabsContent value="login" className="mt-0">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                      <Mail className="w-4 h-4 text-orange-500" />
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      data-testid="login-email"
                      placeholder="you@company.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="login-password" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <Lock className="w-4 h-4 text-orange-500" />
                        Password
                      </Label>
                      <Link 
                        to="/forgot-password" 
                        className="text-sm text-orange-600 hover:text-orange-700 font-semibold"
                      >
                        Forgot?
                      </Link>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      data-testid="login-password"
                      placeholder="Enter password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="login-btn"
                    disabled={loading}
                    className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg shadow-orange-500/30"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    {loading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        SIGN IN
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Demo Quick Access */}
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm text-center text-slate-500 mb-4 font-medium">Quick demo access</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        data-testid="demo-tradie-btn"
                        onClick={() => fillDemoCredentials('tradie')}
                        className="flex items-center justify-center gap-2 h-12 bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 rounded-xl text-base font-semibold text-slate-700 transition-colors"
                      >
                        <HardHat className="w-5 h-5 text-orange-500" />
                        Tradie
                      </button>
                      <button
                        type="button"
                        data-testid="demo-admin-btn"
                        onClick={() => fillDemoCredentials('admin')}
                        className="flex items-center justify-center gap-2 h-12 bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 rounded-xl text-base font-semibold text-slate-700 transition-colors"
                      >
                        <Shield className="w-5 h-5 text-blue-500" />
                        Admin
                      </button>
                    </div>
                  </div>
                </form>
              </TabsContent>

              {/* Get Me Started Form */}
              <TabsContent value="register" className="mt-0">
                {submitted ? (
                  /* Success State */
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Request Sent!</h3>
                    <p className="text-slate-500 mb-6">
                      Thanks for reaching out. We'll be in touch shortly to understand your workflow, tailor 2Tap for you, and discuss pricing options.
                    </p>
                    <Button 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', phone: '', businessName: '', message: '' });
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl h-12 px-8"
                    >
                      Done
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleGetStarted} className="space-y-4">
                    <p className="text-slate-500 text-sm mb-2">
                      Share your details and we'll get in touch to understand your needs and how to tailor 2Tap for you.
                    </p>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-name" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <User className="w-4 h-4 text-orange-500" />
                        Your Name *
                      </Label>
                      <Input
                        id="reg-name"
                        name="name"
                        type="text"
                        data-testid="reg-name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-email" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <Mail className="w-4 h-4 text-orange-500" />
                        Email Address *
                      </Label>
                      <Input
                        id="reg-email"
                        name="email"
                        type="email"
                        data-testid="reg-email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                        className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-phone" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <Phone className="w-4 h-4 text-orange-500" />
                        Phone Number
                      </Label>
                      <Input
                        id="reg-phone"
                        name="phone"
                        type="tel"
                        data-testid="reg-phone"
                        placeholder="0400 000 000"
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-business" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-orange-500" />
                        Business Name
                      </Label>
                      <Input
                        id="reg-business"
                        name="businessName"
                        type="text"
                        data-testid="reg-business"
                        placeholder="Smith Electrical Pty Ltd"
                        value={formData.businessName}
                        onChange={handleFormChange}
                        className="h-14 text-lg bg-slate-50 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="reg-message" className="text-slate-700 font-semibold text-base flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-orange-500" />
                        Message (Optional)
                      </Label>
                      <textarea
                        id="reg-message"
                        name="message"
                        data-testid="reg-message"
                        placeholder="Tell us about your needs..."
                        value={formData.message}
                        onChange={handleFormChange}
                        rows={3}
                        className="w-full text-lg bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 px-4 py-3 resize-none"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      data-testid="register-btn"
                      disabled={loading}
                      className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg rounded-xl mt-2 shadow-lg shadow-orange-500/30"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {loading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          GET ME STARTED
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* Share Modal */}
      <ShareModal open={showShareModal} onClose={() => setShowShareModal(false)} />
    </div>
  );
};

export default Login;
